import { ResponsiveLine } from "@nivo/line";
import { useWindowSize } from "hooks/useWindowSize";
import {
  CATEGORY_COLORS,
  formatAxisValue,
  LegendDot,
} from "components/BooksHomePage/ChartComponents";
import { colors } from "constants/colors";
import { CYMONTHS } from "constants/tax";
import dayjs from "dayjs";
import { CategoryMonthlyData } from "store/apis/financialMetrics";
import { formatAmount } from "utils/foramtAmount";

type CategoriesOverTimeChartProps = {
  categoriesOverTime: CategoryMonthlyData[];
  startDate: string;
  endDate: string;
  emptyLabelType?: "category" | "classification";
};

type LineChartData = {
  id: string;
  color: string;
  data: { x: string; y: number; date: string }[];
};

// Calculate nice round tick interval
const getNiceTickInterval = (range: number): number => {
  if (range <= 0) return 1000;
  const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
  const normalized = range / magnitude;
  if (normalized <= 2) return magnitude / 2;
  if (normalized <= 5) return magnitude;
  return magnitude * 2;
};

// Vertical month labels so they don't overlap on narrow screens.
const MOBILE_TICK_ROTATION = -90;

export const CategoriesOverTimeChart = ({
  categoriesOverTime,
  startDate,
  endDate,
  emptyLabelType = "category",
}: CategoriesOverTimeChartProps) => {
  const categories = categoriesOverTime ?? [];
  const { isMobile } = useWindowSize();
  const isEmpty = !categories.length;

  const generateMonthsInRange = (start: string, end: string) => {
    const months: { date: string; label: string }[] = [];
    let current = dayjs(start).startOf("month");
    const endMonth = dayjs(end).startOf("month");

    while (
      current.isBefore(endMonth) ||
      current.format("YYYY-MM") === endMonth.format("YYYY-MM")
    ) {
      months.push({
        date: current.format("YYYY-MM-01"),
        label: current.format("MMM"),
      });
      current = current.add(1, "month");
    }
    return months;
  };

  const allMonths = generateMonthsInRange(startDate, endDate);
  const emptyMonths = CYMONTHS.map((m) => m.fullName.slice(0, 3));

  const lineData: LineChartData[] = isEmpty
    ? [
        {
          id: "empty",
          color: colors.purple[30],
          data: emptyMonths.map((m) => ({ x: m, y: 0, date: "" })),
        },
      ]
    : categories.slice(0, 6).map((cat, index) => {
        const dataMap = new Map(
          (cat.data ?? []).map((d) => [
            dayjs(d.month).format("YYYY-MM-01"),
            d.amount,
          ])
        );

        return {
          id: cat.name,
          color: cat.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length],
          data: allMonths.map((month) => ({
            x: month.label,
            y: dataMap.get(month.date) ?? 0,
            date: month.date,
          })),
        };
      });

  const lineColors = isEmpty
    ? [colors.purple[30]]
    : categories
        .slice(0, 6)
        .map(
          (cat, index) =>
            cat.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length]
        );

  // Calculate Y axis tick values
  const allValues = lineData.flatMap((series) => series.data.map((d) => d.y));
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);
  const range = dataMax - dataMin;
  const tickInterval = isEmpty ? 20 : getNiceTickInterval(range);
  const scaleMin = isEmpty
    ? 0
    : Math.floor(dataMin / tickInterval) * tickInterval;
  const scaleMax = isEmpty
    ? 100
    : Math.ceil(dataMax / tickInterval) * tickInterval;

  const tickValues: number[] = [];
  for (let i = scaleMin; i <= scaleMax; i += tickInterval) {
    tickValues.push(i);
  }
  const EMPTY_LEGEND_COUNT = 4;

  const emptyLegendLabels = Array.from({ length: EMPTY_LEGEND_COUNT }, (_, i) =>
    emptyLabelType === "classification"
      ? `Classification ${i + 1}`
      : `Category ${i + 1}`
  );

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <div className="t-h-52">
        <ResponsiveLine
          data={lineData}
          margin={{ top: 10, right: 20, bottom: isMobile ? 40 : 30, left: 50 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: scaleMin, max: scaleMax }}
          colors={lineColors}
          curve="monotoneX"
          lineWidth={2}
          pointSize={0}
          enableGridX={false}
          enableGridY={true}
          gridYValues={tickValues}
          axisBottom={{
            tickSize: 0,
            tickPadding: isMobile ? 8 : 15,
            tickRotation: isMobile ? MOBILE_TICK_ROTATION : 0,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 15,
            tickValues,
            format: formatAxisValue,
          }}
          useMesh={true}
          enableSlices="x"
          sliceTooltip={({ slice }) => {
            const nonZeroPoints = slice.points.filter(
              (point) => (point.data.y as number) > 0
            );

            if (nonZeroPoints.length === 0) return null;

            return (
              <div className="t-bg-neutral-100 t-p-3 t-rounded t-text-subtext-sm t-text-white t-min-w-56 sm:t-min-w-40 t-flex t-flex-col t-gap-2">
                <div>
                  {dayjs(slice.points[0]?.data.date as string).format(
                    "MMMM YYYY"
                  )}
                </div>
                {nonZeroPoints.map((point) => (
                  <div key={point.id} className="t-flex t-items-center">
                    <div className="t-flex t-items-center t-justify-between t-w-full">
                      <div className="t-flex t-items-center t-gap-2">
                        <LegendDot color={point.seriesColor} />
                        <span>{point.seriesId}:</span>
                      </div>
                      <span className="t-font-medium">
                        {formatAmount(point.data.y as number)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            );
          }}
          theme={{
            grid: {
              line: {
                stroke: colors.neutral[0],
                strokeDasharray: "4 4",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: colors.text[30],
                  fontSize: 12,
                },
              },
            },
          }}
        />
      </div>
      <div className="t-flex t-flex-wrap t-gap-4">
        {isEmpty
          ? emptyLegendLabels.map((name, i) => (
              <div
                key={name}
                className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-30"
              >
                <LegendDot color={CATEGORY_COLORS[i]} />
                {name}
              </div>
            ))
          : categories.slice(0, 6).map((cat, index) => (
              <div
                key={cat.uuid || cat.name}
                className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-30"
              >
                <LegendDot
                  color={
                    cat.color || CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                  }
                />
                {cat.name}
              </div>
            ))}
      </div>
    </div>
  );
};
